/**
 * Voice + chime helpers.
 *
 * Voices: every catalog mission carries a precomputed `voice_url` pointing
 * to a natural-voice MP3 rendered server-side (OpenAI TTS). The audio is
 * cached forever per (text+voice+model) hash, so the parent / kid hears
 * a *natural* French voice — no robotic browser SpeechSynthesis when the
 * server-side TTS is configured.
 *
 * Fallback chain when calling `speak()`:
 *   1. `voice_url` (server-side TTS MP3) → <audio> playback
 *   2. browser SpeechSynthesis (the previous behaviour) so the experience
 *      degrades gracefully on local dev / missing API key
 *
 * End-of-mission chime stays synthesized (it's not speech, no quality issue).
 */

let audioCtx = null;
let currentVoice = null; // active <audio> element, if any

function getAudioContext() {
  if (typeof window === "undefined") return null;
  if (audioCtx) return audioCtx;
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (!Ctx) return null;
  audioCtx = new Ctx();
  return audioCtx;
}

/**
 * Must be called from a user-gesture handler (button tap). Wakes the
 * AudioContext + plays a single silent <audio> so iOS Safari unlocks audio
 * for the rest of the session. Safe to call multiple times.
 *
 * Without this, the first speak() / startAmbient() after a route change
 * is silently rejected on mobile Safari because the gesture token was
 * consumed by the click-then-navigate sequence.
 */
export function unlockAudio() {
  const ctx = getAudioContext();
  if (ctx && ctx.state === "suspended") {
    ctx.resume().catch(() => {});
  }
  if (typeof Audio !== "undefined") {
    try {
      // A 100ms silent WAV (encoded directly) — iOS treats this as a real
      // play() inside the gesture and unblocks subsequent autoplay.
      const silent = new Audio("data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQAAAAA=");
      silent.volume = 0;
      const p = silent.play();
      if (p && p.catch) p.catch(() => {});
    } catch (_) {}
  }
  // Touch the SpeechSynthesis voices cache early too.
  if (typeof window !== "undefined" && window.speechSynthesis) {
    window.speechSynthesis.getVoices();
  }
}

export function playEndChime() {
  const ctx = getAudioContext();
  if (!ctx) return;
  if (ctx.state === "suspended") ctx.resume().catch(() => {});

  const notes = [
    { freq: 523.25, delay: 0.0 },  // C5
    { freq: 659.25, delay: 0.2 },  // E5
    { freq: 783.99, delay: 0.4 },  // G5
  ];
  notes.forEach(({ freq, delay }) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "sine";
    osc.frequency.value = freq;
    gain.gain.setValueAtTime(0, ctx.currentTime + delay);
    gain.gain.linearRampToValueAtTime(0.18, ctx.currentTime + delay + 0.05);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + delay + 0.8);
    osc.connect(gain).connect(ctx.destination);
    osc.start(ctx.currentTime + delay);
    osc.stop(ctx.currentTime + delay + 0.85);
  });
}

let _noiseBuf = null;
function noiseBuffer(ctx) {
  if (_noiseBuf) return _noiseBuf;
  const len = ctx.sampleRate * 2;
  _noiseBuf = ctx.createBuffer(1, len, ctx.sampleRate);
  const data = _noiseBuf.getChannelData(0);
  for (let i = 0; i < len; i++) data[i] = Math.random() * 2 - 1;
  return _noiseBuf;
}

/**
 * "Pluie d'applaudissements" — bursts of band-passed noise that ramp
 * up like a small crowd cheering. ~2 seconds.
 */
export function playApplause() {
  const ctx = getAudioContext();
  if (!ctx) return;
  if (ctx.state === "suspended") ctx.resume().catch(() => {});

  const t0 = ctx.currentTime;
  const dur = 2.2;
  const master = ctx.createGain();
  master.gain.setValueAtTime(0, t0);
  master.gain.linearRampToValueAtTime(0.35, t0 + 0.4);
  master.gain.linearRampToValueAtTime(0.35, t0 + dur - 0.4);
  master.gain.linearRampToValueAtTime(0,    t0 + dur);
  master.connect(ctx.destination);

  const hp = ctx.createBiquadFilter();
  hp.type = "bandpass"; hp.frequency.value = 2200; hp.Q.value = 0.4;
  hp.connect(master);

  // 24 little claps scattered in 2s with stronger density mid-burst
  for (let i = 0; i < 28; i++) {
    const src = ctx.createBufferSource();
    src.buffer = noiseBuffer(ctx);
    const g = ctx.createGain();
    src.connect(g); g.connect(hp);
    const when = t0 + 0.05 + Math.random() * dur * 0.95;
    const vel  = 0.15 + Math.random() * 0.25;
    g.gain.setValueAtTime(0.0001, when);
    g.gain.linearRampToValueAtTime(vel, when + 0.002);
    g.gain.exponentialRampToValueAtTime(0.0001, when + 0.08);
    src.start(when);
    src.stop(when + 0.1);
  }
}

/**
 * "Yay !" — rising glissando over a bright triangle wave.
 */
export function playCheer() {
  const ctx = getAudioContext();
  if (!ctx) return;
  if (ctx.state === "suspended") ctx.resume().catch(() => {});

  const t0 = ctx.currentTime;
  const o = ctx.createOscillator();
  const g = ctx.createGain();
  o.type = "triangle";
  o.frequency.setValueAtTime(523.25, t0);          // C5
  o.frequency.exponentialRampToValueAtTime(1046.5, t0 + 0.45); // C6
  g.gain.setValueAtTime(0, t0);
  g.gain.linearRampToValueAtTime(0.28, t0 + 0.05);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.6);
  o.connect(g).connect(ctx.destination);
  o.start(t0);
  o.stop(t0 + 0.65);
}

/**
 * Full congratulations cocktail: hourrah chord ding + applause + cheer.
 * Triggered right after a mission is completed, BEFORE the spoken
 * "Bravo!" line so audio layers don't clash.
 */
export function playCongratsBurst() {
  playEndChime();
  setTimeout(() => playCheer(),    250);
  setTimeout(() => playApplause(), 400);
}

function pickFrenchVoice() {
  if (typeof window === "undefined" || !window.speechSynthesis) return null;
  const voices = window.speechSynthesis.getVoices();
  // Prefer non-robotic-sounding voices when several are available.
  return (
    voices.find((v) => /fr[-_]FR/i.test(v.lang) && /natural|google|amélie|thomas/i.test(v.name)) ||
    voices.find((v) => /fr[-_]FR/i.test(v.lang)) ||
    voices.find((v) => v.lang?.toLowerCase().startsWith("fr")) ||
    null
  );
}

function speakWithBrowser(text, character) {
  if (typeof window === "undefined" || !window.speechSynthesis) return;
  window.speechSynthesis.cancel();

  const utter = new SpeechSynthesisUtterance(text);
  utter.lang = "fr-FR";
  // Push pitch up to mimic young voices on the SpeechSynthesis fallback,
  // matching the kid-leaning OpenAI route.
  if (character === "lyria") { utter.pitch = 1.6;  utter.rate = 1.0; }
  else                        { utter.pitch = 1.35; utter.rate = 1.0; }
  utter.volume = 0.95;
  const voice = pickFrenchVoice();
  if (voice) utter.voice = voice;
  window.speechSynthesis.speak(utter);
}

/**
 * Speak a mission line. Prefers the server-side natural-voice MP3 (passed
 * as `voiceUrl`) and falls back to browser SpeechSynthesis.
 *
 * Signature kept compatible with the previous call sites: speak(text, character)
 * still works (browser-only). To use the natural voice, call:
 *   speak(text, character, voiceUrl)
 */
// Per-character pitch fallback when the backend doesn't ship a hint.
// Combined with preservesPitch=false this raises pitch via playbackRate.
// 1.0 → no shift (use this when the audio is already a child voice, e.g. Azure).
const PITCH_BOOST_FALLBACK = {
  neo:   1.18,
  lyria: 1.22,
};

export function speak(text, character = "neo", voiceUrl = null, pitchBoost = null) {
  if (!text) return;
  // Cut any in-flight voice first.
  stopSpeaking();

  if (voiceUrl) {
    try {
      const el = new Audio(voiceUrl);
      el.preload = "auto";
      el.volume = 0.95;
      // Backend-supplied pitch (1.0 when audio is already a child voice)
      // OR per-character fallback for OpenAI adult voices.
      const rate = (typeof pitchBoost === "number" && pitchBoost > 0)
        ? pitchBoost
        : (PITCH_BOOST_FALLBACK[character] || 1.0);
      try {
        el.preservesPitch = rate === 1.0;          // skip the trick when not needed
        el.mozPreservesPitch = rate === 1.0;
        el.webkitPreservesPitch = rate === 1.0;
      } catch (_) {}
      el.playbackRate = rate;
      currentVoice = el;
      el.addEventListener("error", () => {
        // Network/404/decode failure → fall back to browser TTS.
        if (currentVoice === el) currentVoice = null;
        speakWithBrowser(text, character);
      });
      el.play().catch(() => {
        // Autoplay rejection (rare here since the user just tapped) → fallback.
        if (currentVoice === el) currentVoice = null;
        speakWithBrowser(text, character);
      });
      return;
    } catch (_) {
      // fall through to browser
    }
  }
  speakWithBrowser(text, character);
}

export function stopSpeaking() {
  if (typeof window !== "undefined" && window.speechSynthesis) {
    window.speechSynthesis.cancel();
  }
  if (currentVoice) {
    try { currentVoice.pause(); currentVoice.src = ""; } catch (_) {}
    currentVoice = null;
  }
}

/**
 * Browser voices load asynchronously on some platforms; warm them up so the
 * first speak() can pick a French voice on the fallback path.
 */
export function warmUpVoices() {
  if (typeof window === "undefined" || !window.speechSynthesis) return;
  window.speechSynthesis.getVoices();
}
